import { Button, Collapse, type CollapseProps } from 'antd';
import { Check, ChevronRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAddUsersToSpace, useCreateGroup } from '@/api/spaces';
import type { GroupData } from '@/features/user-management/interfaces/interfaces';
import { AddUsersToGroupForm } from '@/features/user-management/pages/components/Forms/AddUsersToGroupForm';
import { AppearanceForm } from '@/features/user-management/pages/components/Forms/AppearanceForm';
import { CreateGroupForm } from '@/features/user-management/pages/components/Forms/CreateGroupForm';

const AddGroups = () => {
  const { spaceId } = useParams<{ spaceId: string }>();
  const navigate = useNavigate();
  const addUsersMutation = useAddUsersToSpace(spaceId || '');
  const createGroupMutation = useCreateGroup(spaceId || '');

  const [groupData, setGroupData] = useState<GroupData>({
    groupName: '',
    groupColor: '',
    users: [],
  });
  const [activeStep, setActiveStep] = useState(1);
  const hasAutoAdvanced = useRef(false);

  useEffect(() => {
    if (activeStep === 2 && groupData.groupColor.trim() !== '' && !hasAutoAdvanced.current) {
      hasAutoAdvanced.current = true;
      setActiveStep(3);
    }

    if (groupData.groupColor.trim() === '') {
      hasAutoAdvanced.current = false;
    }
  }, [activeStep, groupData.groupColor]);

  const items = [
    [
      {
        key: '1',
        label: (
          <div className={`flex items-center gap-2 w-full text-left font-medium text-base text-[#C1C1C1]`} onClick={() => setActiveStep(1)}>
            <div
              className={`w-6.5 h-6.5 rounded-[5px] flex items-center justify-center ${activeStep === 1 ? 'bg-primary-gold text-white' : groupData.groupName.trim() !== '' ? 'bg-[#E0C892] text-white' : 'border border-[#E9ECEF]'}`}
            >
              {groupData.groupName.trim() === '' ? '1' : <Check className="w-[21px] h-[21px]" />}
            </div>
            <span className={`${activeStep === 1 && 'text-black'}`}>Create group</span>
          </div>
        ),
        children: <CreateGroupForm groupData={groupData} setActiveStep={setActiveStep} setGroupData={setGroupData} />,
        headerClass: `${activeStep === 1 ? 'bg-[#F2F2F2]' : 'bg-white'} px-4 py-3 rounded-t-[5px] flex items-center justify-between`,
      },
    ],
    [
      {
        collapsible: groupData.groupName.trim() === '' ? 'disabled' : 'header',
        key: '2',
        onClick: () => (groupData.groupName.trim() === '' ? setActiveStep(1) : setActiveStep(2)),
        label: (
          <div
            className={`flex items-center gap-2 !w-[910px] text-left font-medium text-base text-[#C1C1C1]`}
            onClick={() => setActiveStep(1)}
          >
            <div
              className={`w-6.5 h-6.5 rounded-[5px] flex items-center justify-center ${activeStep === 2 ? 'bg-primary-gold text-white' : groupData.groupName.trim() !== '' && groupData.groupColor.trim() !== '' ? 'bg-[#E0C892] text-white' : 'border border-[#E9ECEF]'}`}
            >
              {groupData.groupName.trim() === '' || groupData.groupColor.trim() === '' ? '2' : <Check className="w-[21px] h-[21px]" />}
            </div>
            <span className={`${activeStep === 2 && 'text-black'}`}>Appearance</span>
          </div>
        ),
        children: <AppearanceForm groupData={groupData} setGroupData={setGroupData} />,
        headerClass: `${activeStep === 2 ? 'bg-[#F2F2F2]' : 'bg-white'} px-4 py-3 rounded-t-[5px] flex items-center justify-between`,
      },
    ],
    [
      {
        collapsible: groupData.groupName.trim() === '' || groupData.groupColor.trim() === '' ? 'disabled' : 'header',
        key: '3',
        onClick: () =>
          groupData.groupName.trim() === '' ? setActiveStep(1) : groupData.groupColor.trim() === '' ? setActiveStep(2) : setActiveStep(3),
        label: (
          <div className={`flex items-center gap-2 !w-[910px] text-left font-medium text-base text-[#C1C1C1]`}>
            <div
              className={`w-6.5 h-6.5 rounded-[5px] flex items-center justify-center ${activeStep === 3 ? 'bg-primary-gold text-white' : 'border border-[#E9ECEF]'}`}
            >
              3
            </div>
            <span className={`${activeStep === 3 && 'text-black'} rounded`}>Add users to group</span>
          </div>
        ),
        children: <AddUsersToGroupForm groupData={groupData} setGroupData={setGroupData} />,
        headerClass: `${activeStep === 3 ? 'bg-[#F2F2F2]' : 'bg-white'} px-4 py-3 rounded-t-[5px] flex items-center justify-between`,
      },
    ],
  ];

  return (
    <div className="flex flex-col gap-y-4 h-full mt-4">
      <div className="flex flex-col gap-y-4 border rounded-lg p-2 bg-white h-[calc(100vh-150px)]">
        {items.map((item: any) => {
          return (
            <Collapse
              accordion
              activeKey={String(activeStep)}
              bordered={false}
              className={`bg-white border border-[#E9ECEF] rounded-[5px]`}
              expandIcon={({ isActive }) => (
                <ChevronRight
                  className={`w-6 h-6 transition-all duration-300 ${isActive ? 'rotate-90 text-black' : 'rotate-0 text-[#C1C1C1]'}`}
                />
              )}
              expandIconPosition="end"
              items={item as CollapseProps['items']}
              key={item.key}
              onChange={(key) => setActiveStep(Number(key))}
            />
          );
        })}
        <div className="flex justify-end gap-4">
          <Button
            className={`hidden rounded-md w-[136px] h-[50px] bg-[#E9ECEF] hover:!bg-[#EFEFEF] items-center justify-center focus:ring-0`}
            // OnClick={handlePrev}
          >
            Cancel
          </Button>
          <Button
            className={`text-white rounded-md w-[136px] h-[50px] ${Object.keys(groupData.users).length === 0 || addUsersMutation.isPending ? 'bg-[#A6A6A6]' : 'bg-primary-gold hover:!bg-gold-darker'} flex items-center justify-center focus:ring-0`}
            disabled={
              groupData.groupName.trim() === '' ||
              groupData.groupColor.trim() === '' ||
              groupData.users.length === 0 ||
              createGroupMutation.isPending
            }
            loading={createGroupMutation.isPending}
            onClick={async () => {
              await createGroupMutation.mutateAsync({
                name: groupData.groupName,
                space_id: spaceId || '',
                color: groupData.groupColor,
                users: Object.keys(groupData.users).map((k: any) => groupData.users[k].user_id),
              });
              navigate(-1);
            }}
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddGroups;
